/**
 * Restaurant info form logic, kept pure so the rules are testable without rendering.
 *
 * Validation runs BEFORE the request rather than relying on the server's 400, because a
 * mistyped phone number here means the agent can't reach staff — a silent failure the
 * owner wouldn't discover until a caller needed a human.
 */
import type { GreetingLanguage, RestaurantProfile } from "@glowin/schemas";

/** Editable shape: strings throughout, since that's what inputs produce. */
export interface ProfileForm {
  name: string;
  address: string;
  timezone: string;
  /** Language the agent greets in; it follows the caller after that. */
  preferredLanguage: GreetingLanguage;
  staffPhone: string;
  /** Inbound number. Read-only unless the caller is a glowin operator. */
  phoneE164: string;
}

export const EMPTY_FORM: ProfileForm = {
  name: "",
  address: "",
  timezone: "America/Los_Angeles",
  preferredLanguage: "en",
  staffPhone: "",
  phoneE164: ""
};

export function toForm(p: RestaurantProfile | undefined): ProfileForm {
  if (!p) return { ...EMPTY_FORM };
  return {
    name: p.displayName,
    address: p.address ?? "",
    timezone: p.timezone,
    preferredLanguage: p.preferredLanguage,
    staffPhone: p.staffPhoneE164 ?? "",
    phoneE164: p.phoneE164
  };
}

const E164 = /^\+[1-9]\d{7,14}$/;

/** Strip spaces, dashes and brackets people naturally type into a phone field. */
export function normalizePhone(raw: string): string {
  return raw.replace(/[\s()\-.]/g, "");
}

export type FieldErrors = Partial<Record<keyof ProfileForm, string>>;
// preferredLanguage needs no validation: it comes from a fixed list, not a text box.

export function validate(form: ProfileForm): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.name.trim()) {
    errors.name = "A name is required — the agent says it when answering.";
  } else if (form.name.trim().length > 120) {
    errors.name = "Keep the name under 120 characters.";
  }

  if (!form.timezone.trim()) {
    errors.timezone = "A timezone is required — opening hours are read in it.";
  }

  if (form.address.length > 300) {
    errors.address = "Keep the address under 300 characters.";
  }

  // Optional, but if given it must be dialable — a typo here strands callers.
  const staff = normalizePhone(form.staffPhone);
  if (staff && !E164.test(staff)) {
    errors.staffPhone = "Use the full international form, e.g. +15551234567.";
  }

  const inbound = normalizePhone(form.phoneE164);
  if (!inbound) {
    errors.phoneE164 = "The customer-facing number is required.";
  } else if (!E164.test(inbound)) {
    errors.phoneE164 = "Use the full international form, e.g. +15074362478.";
  }

  return errors;
}

export function isValid(form: ProfileForm): boolean {
  return Object.keys(validate(form)).length === 0;
}

/** Build the request body. Call only when `isValid`. */
export function toProfile(form: ProfileForm): RestaurantProfile {
  const address = form.address.trim();
  const staff = normalizePhone(form.staffPhone);
  return {
    displayName: form.name.trim(),
    phoneE164: normalizePhone(form.phoneE164),
    ...(staff ? { staffPhoneE164: staff } : {}),
    ...(address ? { address } : {}),
    timezone: form.timezone.trim(),
    preferredLanguage: form.preferredLanguage
  };
}

/** Has anything actually changed? Drives the Save button and unsaved-changes warnings. */
export function isDirty(form: ProfileForm, original: ProfileForm): boolean {
  return (Object.keys(form) as (keyof ProfileForm)[]).some((k) => form[k] !== original[k]);
}
